/**
 * components/object/ConflictInspector.jsx — Iter 51.
 *
 * Operational-record inspector for promoted Utility Conflicts. Reuses
 * Iter 47 ObjectInspector. Header surfaces conflict-specific facts
 * (layers, clearance, station). DRAWING-FIRST — no separate workflow.
 *
 * NOTE: distinct from the legacy informational `components/viewer/
 * ConflictInspector.jsx` (read-only ephemeral crossings). This one
 * handles persisted operational records.
 */
import React from "react";
import { X, AlertTriangle, Navigation2 } from "lucide-react";
import ObjectInspector from "@/components/object/ObjectInspector";
import ObjectRelations from "@/components/object/ObjectRelations";

const KIND_LABEL = {
  crossing: "Crossing",
  clearance: "Clearance",
  alignment_conflict: "Alignment Conflict",
  buried_unknown: "Buried Unknown",
  depth_unknown: "Depth Unknown",
  other: "Other",
};

export default function OperationalConflictInspector({ conflict, onClose }) {
  if (!conflict) return null;
  const stationLabel = conflict.station != null
    ? `STA ${formatStation(conflict.station)}`
    : null;

  const header = (
    <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono uppercase tracking-wider"
          data-testid="conflict-header-meta">
      <span className="px-1.5 py-0.5 rounded border border-[var(--st-escalated,#ef4444)] bg-[var(--st-escalated-soft,rgba(239,68,68,0.12))] text-[var(--st-escalated,#ef4444)]">
        <AlertTriangle size={9} className="inline -mt-0.5 mr-1" />
        {KIND_LABEL[conflict.kind] || conflict.kind || "Conflict"}
      </span>
      {conflict.layer_a && conflict.layer_b && (
        <span className="text-[var(--fop-text-2)]">
          {conflict.layer_a} × {conflict.layer_b}
        </span>
      )}
      {conflict.estimated_clearance_ft != null && (
        <span className="text-[var(--fop-text-3)]">
          ≈ {conflict.estimated_clearance_ft.toFixed(1)} ft clearance
        </span>
      )}
      {stationLabel && (
        <span className="text-[var(--fop-text-2)]">
          <Navigation2 size={9} className="inline -mt-0.5 mr-1 text-[var(--fop-primary)]" />
          <b className="text-[var(--fop-primary)]">{stationLabel}</b>
        </span>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full" data-testid="conflict-inspector-v2"
          data-conflict-id={conflict.id}>
      <div className="px-4 py-3 border-b border-[var(--fop-border)] flex items-start gap-3">
        <AlertTriangle size={14} className="text-[var(--st-escalated,#ef4444)] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
            Utility Conflict
          </div>
          <div className="font-mono text-sm font-bold text-[var(--fop-text)] truncate"
                data-testid="conflict-inspector-title">
            {conflict.title}
          </div>
        </div>
        <button onClick={onClose}
                 className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] -mr-1"
                 aria-label="Close inspector" data-testid="conflict-inspector-close">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <ObjectInspector
          objectType="conflict" objectId={conflict.id}
          headerSlot={header}
        />
        <div className="border-t border-[var(--fop-border)] p-3">
          <ObjectRelations objectType="conflict" objectId={conflict.id} />
        </div>
      </div>
    </div>
  );
}

function formatStation(s) {
  if (s == null) return "—";
  const block = Math.floor(s / 100);
  const rem = s - block * 100;
  if (Math.abs(rem - Math.round(rem)) < 0.05) {
    return `${block}+${String(Math.round(rem)).padStart(2, "0")}`;
  }
  return `${block}+${rem.toFixed(2).padStart(5, "0")}`;
}
